import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, Clock, Loader2, RotateCcw } from "lucide-react";
import { api, fmtErr } from "./shared";

const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", weekday: "short" });

const PunchForm = ({ it, onDone }) => {
  const missingIn = it.issues.some((x) => x.startsWith("no morning check-in"));
  const [kind, setKind] = useState(missingIn && it.out ? "in" : "out");
  const [time, setTime] = useState(missingIn && it.out ? "09:00" : "17:30");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/biometric/review/punch", { email: it.email, day: it.day, time, kind });
      const st = data.row?.status ? data.row.status.replace("_", " ") : "updated";
      toast.success(`${kind === "in" ? "Check-in" : "Check-out"} ${time} added — ${fmtDay(it.day)} is now ${st}`);
      window.dispatchEvent(new Event("nx-attendance-changed"));
      onDone();
    } catch (e) { toast.error(fmtErr(e)); }
    setSaving(false);
  };
  const id = `${it.email}-${it.day}`;
  return (
    <div className="w-full flex flex-wrap items-center gap-2 pt-1" data-testid={`review-punch-form-${id}`}>
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-nx-navy2 border border-white/15 text-xs text-white px-2 py-1.5" data-testid={`review-punch-kind-${id}`}>
        <option value="out">Check-out</option>
        <option value="in">Check-in</option>
      </select>
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-nx-navy2 border border-white/15 text-xs text-white px-2 py-1.5" data-testid={`review-punch-time-${id}`} />
      <button onClick={save} disabled={saving || !time} className="text-[10px] uppercase tracking-wider bg-nx-gold text-nx-navy font-semibold px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`review-punch-save-${id}`}>
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />} Add punch
      </button>
      <span className="text-[10px] text-slate-500">Counts like a device punch — the day is re-evaluated and the slip updates.</span>
    </div>
  );
};

export const ReviewQueue = ({ isCeo }) => {
  const [v, setV] = useState();
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState("");
  const [fixing, setFixing] = useState("");
  const load = useCallback(() => api.get("/biometric/review").then(({ data }) => setV(data)).catch((e) => setV({ error: fmtErr(e) })), []);
  useEffect(() => { load(); window.addEventListener("nx-attendance-changed", load); return () => window.removeEventListener("nx-attendance-changed", load); }, [load]);
  const resolve = async (it, undo = false) => {
    setBusy(it.email + it.day);
    try { await api.post("/biometric/review/resolve", { email: it.email, day: it.day, undo }); toast.success(undo ? "Reopened" : "Marked as reviewed"); await load(); } catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };
  const items = (v?.items || []).filter((i) => showDone || !i.resolved);
  return (
    <div className="border border-amber-500/30 bg-amber-500/[0.04] p-4 space-y-3" data-testid="attendance-review-queue">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle size={15} className="text-amber-300 shrink-0" />
        <h3 className="text-white text-sm flex-1 min-w-[160px]">Punches needing review <span className="text-slate-500 font-normal">· since 1 Oct 2026</span></h3>
        {v && !v.error && <span className="text-[11px] px-2 py-0.5 border border-amber-500/40 text-amber-200" data-testid="review-open-count">{v.open} open</span>}
        <label className="text-[11px] text-slate-400 inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} data-testid="review-show-done" /> show reviewed</label>
      </div>
      <p className="text-[11px] text-slate-500">Rules from 1 Oct 2026 (office and WFH): check-in after 9:30:00 AM or any check-in after 2:00:00 PM = half day; check-out time never causes LOP. Days listed here have a missing or conflicting punch — add the missing punch time directly, or mark it reviewed.</p>
      {!v && <Loader2 size={16} className="animate-spin text-slate-400" />}
      {v?.error && <p className="text-xs text-red-300">{v.error}</p>}
      {v && !v.error && items.length === 0 && <p className="text-xs text-slate-500" data-testid="review-empty">Nothing waiting for review.</p>}
      <ul className="divide-y divide-white/5">
        {items.map((it) => {
          const id = it.email + it.day;
          return (
            <li key={id} className={`py-2.5 flex flex-wrap items-start gap-x-4 gap-y-1 ${it.resolved ? "opacity-50" : ""}`} data-testid={`review-row-${it.email}-${it.day}`}>
              <div className="w-full sm:w-auto sm:flex-1 min-w-0 break-words">
                <p className="text-sm text-white">{it.name} <span className="text-slate-500 text-xs">· {fmtDay(it.day)}{it.wfh ? " · WFH" : ""}</span></p>
                <p className="text-[11px] text-slate-400">in {it.in || "—"} · out {it.out || "—"} · <span className={it.status === "half_day" ? "text-amber-300" : "text-slate-300"}>{it.status.replace("_", " ")}</span></p>
                <p className="text-[11px] text-amber-200/90">{it.issues.join(" · ")}</p>
                {it.resolved && it.resolution && <p className="text-[11px] text-slate-500">Reviewed: {it.resolution}</p>}
              </div>
              {isCeo && (it.resolved
                ? <button onClick={() => resolve(it, true)} disabled={busy === id} className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-white inline-flex items-center gap-1 shrink-0" data-testid={`review-undo-${it.email}-${it.day}`}><RotateCcw size={11} /> Reopen</button>
                : (
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button onClick={() => setFixing(fixing === id ? "" : id)} className="text-[10px] uppercase tracking-wider text-nx-gold hover:text-white border border-nx-gold/50 px-2 py-1 inline-flex items-center gap-1" data-testid={`review-fix-${it.email}-${it.day}`}><Clock size={11} /> {fixing === id ? "Cancel" : "Fix punch"}</button>
                    <button onClick={() => resolve(it)} disabled={busy === id} className="text-[10px] uppercase tracking-wider text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 px-2 py-1 inline-flex items-center gap-1" data-testid={`review-resolve-${it.email}-${it.day}`}><Check size={11} /> Reviewed</button>
                  </div>
                ))}
              {isCeo && !it.resolved && fixing === id && <PunchForm it={it} onDone={() => { setFixing(""); load(); }} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
